import {
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { lazy, Suspense, useState } from 'react';

import { Input, Type } from '@/components/form/Input';
import { Tooltip } from '@/components/Tooltip';
import { CapabilityParameter } from '@/hooks/useCapabilities';

const MonacoEditor = lazy(() =>
  import('@/sections/attacks/templateRefinement/components/MonacoEditor').then(
    m => ({ default: m.MonacoEditor })
  )
);

interface ParameterInputProps {
  param: CapabilityParameter;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  readOnly?: boolean;
  isRequired?: boolean;
  onBlur?: (paramName: string) => void;
}

function isPasswordField(param: CapabilityParameter): boolean {
  const checkText = (text: string | undefined) =>
    text?.toLowerCase().includes('password') ?? false;

  return checkText(param.name) || checkText(param.description);
}

function isYamlField(param: CapabilityParameter): boolean {
  return param.description?.toLowerCase().includes('yaml') ?? false;
}

function MultiSelectCheckboxes({
  options,
  value,
  onChange,
  paramName,
  isRequired,
  error,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  paramName: string;
  isRequired: boolean;
  error?: string;
}) {
  const selected = new Set(
    value
      ? value
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
      : []
  );

  const handleToggle = (option: string) => {
    const next = new Set(selected);
    if (next.has(option)) {
      next.delete(option);
    } else {
      next.add(option);
    }
    onChange(Array.from(next).join(','));
  };

  return (
    <div>
      <div
        className={`border-chariot-stroke bg-chariot-background-primary max-h-48 overflow-y-auto rounded-[2px] border p-2 ${error ? 'border-red-500' : ''}`}
        role="group"
        aria-label={`${paramName} multi-select${isRequired ? ' (required)' : ''}`}
      >
        {options.map(opt => (
          <label
            key={opt}
            className="text-chariot-text-primary flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-gray-500/10"
          >
            <input
              type="checkbox"
              checked={selected.has(opt)}
              onChange={() => handleToggle(opt)}
              className="accent-chariot-brand-primary"
            />
            <span className="truncate" title={opt}>
              {opt}
            </span>
          </label>
        ))}
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

export function ParameterInput({
  param,
  value,
  onChange,
  error,
  readOnly = false,
  isRequired = false,
  onBlur,
}: ParameterInputProps) {
  const [expanded, setExpanded] = useState(!!value);
  const yamlField = isYamlField(param);

  if (readOnly) {
    return (
      <div className="flex items-center gap-4">
        <div className={`${isRequired ? 'w-48' : 'w-40'} shrink-0`}>
          <span className="text-chariot-text-primary text-sm font-bold">
            {param.name}
          </span>
        </div>
        <div className="flex-1">
          <span className="text-chariot-text-secondary text-sm">
            {(param.default || value || 'Not configured').split(',').join(', ')}
          </span>
        </div>
      </div>
    );
  }

  if (yamlField) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <label className="text-chariot-text-primary text-sm font-medium">
            {param.name}
            {isRequired && <span className="ml-0.5 text-red-500">*</span>}
          </label>
          {param.description && (
            <Tooltip
              title={param.description}
              placement="top"
              tooltipClassName="max-w-sm whitespace-normal break-words"
            >
              <InformationCircleIcon className="text-chariot-stroke size-4" />
            </Tooltip>
          )}
          {!expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-chariot-primary text-xs hover:underline"
            >
              Edit YAML
            </button>
          )}
          {expanded && value && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setExpanded(false);
              }}
              className="text-xs text-red-400 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
        {expanded && (
          <Suspense
            fallback={
              <div className="bg-chariot-background-primary border-chariot-stroke h-48 rounded border p-2 text-sm text-gray-500">
                Loading editor...
              </div>
            }
          >
            <div
              className={`overflow-hidden rounded border ${
                error ? 'border-red-500' : 'border-chariot-stroke'
              }`}
            >
              <MonacoEditor
                content={value}
                onChange={onChange}
                validator={null}
                height="200px"
              />
            </div>
          </Suspense>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <div className="flex w-40 shrink-0 items-center gap-2">
        <label className="text-chariot-text-primary text-sm font-medium">
          {param.name}
          {isRequired && <span className="ml-0.5 text-red-500">*</span>}
        </label>
        {param.description && (
          <Tooltip
            title={param.description}
            placement="top"
            tooltipClassName="max-w-sm whitespace-normal break-words"
          >
            <InformationCircleIcon className="text-chariot-stroke size-4" />
          </Tooltip>
        )}
      </div>
      <div className="flex-1">
        {param.type === 'bool' ? (
          <div className="relative">
            <select
              name={param.name}
              value={value || param.default || 'false'}
              onChange={e => onChange(e.target.value)}
              className={`border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary block w-full appearance-none rounded-[2px] border p-1.5 pr-8 pl-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none ${error ? 'border-red-500' : ''}`}
              aria-label={`${param.name} select${isRequired ? ' (required)' : ''}`}
            >
              <option value="false">Disabled</option>
              <option value="true">Enabled</option>
            </select>
            <ChevronDownIcon className="text-chariot-text-secondary pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2" />
          </div>
        ) : param.type === '[]string' &&
          param.options &&
          param.options.length > 0 ? (
          <MultiSelectCheckboxes
            options={param.options}
            value={value}
            onChange={onChange}
            paramName={param.name}
            isRequired={isRequired}
            error={error}
          />
        ) : param.options && param.options.length > 0 ? (
          <div className="relative">
            <select
              name={param.name}
              value={value}
              onChange={e => onChange(e.target.value)}
              className={`border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary block w-full appearance-none rounded-[2px] border p-1.5 pr-8 pl-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none ${error ? 'border-red-500' : ''}`}
              aria-label={`${param.name} select${isRequired ? ' (required)' : ''}`}
            >
              <option value="">Select {param.name.toLowerCase()}...</option>
              {param.options.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="text-chariot-text-secondary pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2" />
          </div>
        ) : (
          <Input
            name={param.name}
            type={isPasswordField(param) ? Type.PASSWORD : undefined}
            password={isPasswordField(param)}
            placeholder={
              param.default || `Enter ${param.name.toLowerCase()}...`
            }
            value={value}
            onChange={e => onChange(e.target.value)}
            onBlur={() => onBlur?.(param.name)}
            error={error}
            className="w-full"
            aria-label={`${param.name} input${isRequired ? ' (required)' : ''}`}
          />
        )}
      </div>
    </div>
  );
}
